import React, { useState, useEffect } from 'react';
import { string, number, func } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import css from './AgeGate.module.css';

const STORAGE_KEY = 'ageVerified';
const MINIMUM_AGE = parseInt(process.env.REACT_APP_AGE_GATE_MINIMUM || '18', 10);

/**
 * AgeGate — age verification modal for 18+ (or custom age) marketplaces.
 *
 * Shows a blocking modal when the user first visits.
 * Confirmation is stored in localStorage (click-through only, not legally verified).
 * Dismissed modal is remembered for 30 days.
 *
 * Usage (in App.js or LandingPage):
 *   {features.ageVerification && <AgeGate />}
 *
 * @param {number} [props.minimumAge] — minimum required age (default: 18)
 * @param {Function} [props.onConfirm] — called when user confirms age
 * @param {Function} [props.onDeny] — called when user denies age (default: redirect to google.com)
 */
const AgeGate = ({ minimumAge, onConfirm, onDeny, className }) => {
  const [visible, setVisible] = useState(false);

  const age = minimumAge || MINIMUM_AGE;

  useEffect(() => {
    if (typeof localStorage === 'undefined') return;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try {
        const { confirmedAt } = JSON.parse(raw);
        const EXPIRY_MS = 30 * 24 * 3600 * 1000;
        if (Date.now() - confirmedAt < EXPIRY_MS) return; // still valid
      } catch (_) {}
    }
    setVisible(true);
  }, []);

  const handleConfirm = () => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ confirmedAt: Date.now(), age }));
    }
    setVisible(false);
    onConfirm && onConfirm();
  };

  const handleDeny = () => {
    if (onDeny) {
      onDeny();
    } else {
      window.location.href = 'https://www.google.com';
    }
  };

  if (!visible) return null;

  return (
    <div className={classNames(css.overlay, className)}>
      <div className={css.modal}>
        <p className={css.icon}>🔞</p>
        <h2 className={css.title}>
          <FormattedMessage id="AgeGate.title" values={{ age }} />
        </h2>
        <p className={css.message}>
          <FormattedMessage id="AgeGate.message" values={{ age }} />
        </p>
        <div className={css.actions}>
          <button type="button" onClick={handleConfirm} className={css.confirmBtn}>
            <FormattedMessage id="AgeGate.confirm" values={{ age }} />
          </button>
          <button type="button" onClick={handleDeny} className={css.denyBtn}>
            <FormattedMessage id="AgeGate.deny" />
          </button>
        </div>
        <p className={css.disclaimer}>
          <FormattedMessage id="AgeGate.disclaimer" />
        </p>
      </div>
    </div>
  );
};

AgeGate.defaultProps = { minimumAge: MINIMUM_AGE, onConfirm: null, onDeny: null, className: null };
AgeGate.propTypes = { minimumAge: number, onConfirm: func, onDeny: func, className: string };

export { STORAGE_KEY, MINIMUM_AGE };
export default AgeGate;
